import type { ReactNode } from "react";
import { useOfferWorkspace } from "@/hooks/use-offer-workspace";
import { useOfferIdeaCards } from "@/hooks/use-offer-idea-cards";
import { useOfferLines } from "@/hooks/use-offers";
import type { Offer } from "@/hooks/use-offers";
import {
  UPCOMING_BRANCHES,
  budsjettStatus,
  grunnlagStatus,
  ideStatus,
  type BranchStatus,
  type WorkspaceBranch,
} from "@/lib/tilbudsarbeidsrom/branches";
import { WorkspaceBranchTabs } from "./WorkspaceBranchTabs";
import { GrunnlagBranch } from "./GrunnlagBranch";
import { IdeaBoardBranch } from "./IdeaBoardBranch";
import { cardsUsedInBudget } from "@/lib/tilbudsarbeidsrom/idea-board";
import { UpcomingBranch } from "./UpcomingBranch";

interface OfferWorkspaceBranchesProps {
  enabled: boolean;
  branch: WorkspaceBranch;
  offer: Offer;
  lineCount: number;
  /** Timesatsen nye linjer fra katalogen prises med (tilbudets egen eller systemets). */
  hourlyRate: number;
  customerLabel: string;
  description: string;
  onDescriptionChange: (next: string) => void;
  readOnly: boolean;
  /** Budsjett-grenen: dagens tilbudsside under headeren. */
  children: ReactNode;
}

/**
 * Grenene i tilbudsarbeidsrommet (VIB-2979). Bor inne i OfferEditPage, så
 * headeren og utkastet (tittel, beskrivelse, påslag) blir værende ved grenbytte.
 * Med flagget av vises bare Budsjett, som før.
 */
export function OfferWorkspaceBranches({
  enabled,
  branch,
  offer,
  lineCount,
  hourlyRate,
  customerLabel,
  description,
  onDescriptionChange,
  readOnly,
  children,
}: OfferWorkspaceBranchesProps) {
  const { data: workspace } = useOfferWorkspace(enabled ? offer.id : undefined);
  const { data: ideaCards = [] } = useOfferIdeaCards(enabled ? offer.id : undefined);
  const { data: lines = [] } = useOfferLines(enabled ? offer.id : undefined);
  if (!enabled) return <>{children}</>;

  const usedIds = cardsUsedInBudget(lines);

  const statuses: Record<WorkspaceBranch, BranchStatus> = {
    grunnlag: grunnlagStatus({
      description,
      targetAudience: workspace?.target_audience,
      budgetFrameNok: workspace?.budget_frame_nok,
    }),
    ide: ideStatus(ideaCards.length, ideaCards.filter((c) => usedIds.has(c.id)).length),
    budsjett: budsjettStatus(lineCount, offer.status),
    presentasjon: "kommer",
    tilbud: "kommer",
  };

  return (
    <>
      <WorkspaceBranchTabs offerId={offer.id} active={branch} statuses={statuses} />
      {branch === "budsjett" && children}
      {branch === "grunnlag" && (
        <GrunnlagBranch
          offer={offer}
          customerLabel={customerLabel}
          description={description}
          onDescriptionChange={onDescriptionChange}
          readOnly={readOnly}
        />
      )}
      {branch === "ide" && (
        <IdeaBoardBranch
          offer={offer}
          hourlyRate={hourlyRate}
          // B20: akseptert tilbud gjør Idé skrivebeskyttet, som budsjettet.
          readOnly={readOnly || offer.status === "accepted"}
        />
      )}
      {UPCOMING_BRANCHES.includes(branch) && <UpcomingBranch branch={branch} />}
    </>
  );
}
